import api from "./base";

/**
 * Tenant-segmentation home-surface pointer (Wave 2).
 *
 * `classic` = Filing (Platform 1, the legacy /buildings shell).
 * `portal`  = Tracker (Platform 2, the new /portal shell).
 *
 * UI labels are deliberately decoupled from the wire values — UX guidance
 * is to render "Filing" / "Tracker" everywhere, never "Classic" / "Portal".
 */
export type PlatformHome = "classic" | "portal";

/**
 * Subset of `account.Company` exposed via the `/me` endpoint.
 *
 * The DRF surface uses snake_case but `api/base.ts` runs `camelizeKeys` on
 * every JSON response — so `platform_home` arrives here as `platformHome`,
 * mirroring how `is_consultant` becomes `isConsultant` elsewhere. The audit
 * columns (`platform_home_set_at` / `_set_by`) are deliberately omitted from
 * `CompanySerializer` and never reach the client.
 */
export interface CompanyShape {
  id: string;
  name: string;
  isConsultant?: boolean;
  platformHome?: PlatformHome;
  enabledModules?: string[];
  [x: string]: unknown;
}

export interface UserType {
  id: string;
  firstName: string;
  lastName: string;
  companyAdmin: boolean;
  email: string;
  preferences?: UserPreferencesType;
  isStaff?: boolean;
  trackingEnabled?: boolean;
  company?: CompanyShape;
  [x: string]: unknown;
}

export interface UserPreferencesType {
  id: string;
  emailAllBuildingUpdates: boolean;
  emailNoPortfolioBuildingUpdates: boolean;
  emailPortfolioBuildingUpdates: boolean;
  [x: string]: unknown;
}

interface UserUpdateData {
  firstName?: string;
  lastName?: string;
  companyName?: string;
}

export const updateUser = (
  userId: string,
  updateData: UserUpdateData,
): Promise<UserType> =>
  api
    .patch(`user/${userId}/`, updateData)
    .then((response) => response.data as UserType)
    .catch((error) => {
      console.error("There was an error updating the user information", error);
      throw error;
    });

interface UserCreationData {
  email: string;
  firstName: string;
  lastName: string;
  companyAdmin: boolean;
}
export const createCompanyUser = (
  companyId: string,
  userData: UserCreationData,
): Promise<UserType> =>
  api
    .post(`company/${companyId}/create_user/`, userData)
    .then((response) => response.data as UserType)
    .catch((error) => {
      console.error("There was an error creating the user", error);
      throw error;
    });

// --- Portal Team surface (invite lifecycle) -------------------------------
export type InviteStatus = "active" | "pending" | "expired" | "revoked";

export interface TeamMember {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyAdmin: boolean;
  inviteStatus: InviteStatus;
  isSelf: boolean;
}

export interface TeamData {
  companyName: string;
  viewerIsAdmin: boolean;
  members: TeamMember[];
}

// Bodies authored camelCase (base.ts decamelizes the wire -> user_id);
// responses arrive camelCase.
export const fetchTeam = (): Promise<TeamData> =>
  api.get("company/team/").then((r) => r.data as TeamData);

export const resendInvite = (
  userId: string,
): Promise<{ inviteStatus: InviteStatus }> =>
  api.post("company/resend_invite/", { userId }).then((r) => r.data);

export const revokeInvite = (
  userId: string,
): Promise<{ inviteStatus: InviteStatus }> =>
  api.post("company/revoke_invite/", { userId }).then((r) => r.data);

export interface CompanyData {
  name: string;
  id: string;
  users?: UserType[];
  platformHome?: PlatformHome;
}
export const createCompany = (name: string): Promise<CompanyData> =>
  api
    .post("company/", { name })
    .then((response) => response.data as CompanyData)
    .catch((error) => {
      console.error("There was an error creating the company", error);
      throw error;
    });

/**
 * Patches a Company.
 *
 * TODO(platform-home, Wave 2): the staff-side flip currently lives in the
 * Django admin (audit columns stamp via the admin save hook). The matching
 * customer-facing PATCH route is not yet wired in `account.viewsets`. Until
 * then this helper hits `PATCH /company/:id/` optimistically — staff have
 * write access via DRF default permissions; non-staff will 403 (which the
 * UI already gates: the segmented control is staff-only).
 *
 * When the dedicated route ships (per Wave 3 acceptance criteria), swap the
 * URL here without touching call sites.
 */
export interface CompanyUpdateData {
  name?: string;
  platformHome?: PlatformHome;
}
export const updateCompany = (
  companyId: string,
  updateData: CompanyUpdateData,
): Promise<CompanyData> =>
  api
    .patch(`company/${companyId}/`, updateData)
    .then((response) => response.data as CompanyData)
    .catch((error) => {
      console.error("There was an error updating the company", error);
      throw error;
    });

export const fetchAllCompanies = (): Promise<CompanyData[]> =>
  api
    .get(`company/`)
    .then((response) => response.data as CompanyData[])
    .catch((error) => {
      console.error("There was an error fetching the company detail", error);
      throw error;
    });

export const fetchCompanyDetails = (id: string): Promise<CompanyData> =>
  api
    .get(`company/${id}/`)
    .then((response) => response.data as CompanyData)
    .catch((error) => {
      console.error("There was an error fetching the company detail", error);
      throw error;
    });
export const removeUser = (userId: string) =>
  api
    .delete(`user/${userId}/`)
    .then((response) => response.data)
    .catch((error) => {
      console.error("There was an error removing the user", error);
      throw error;
    });

export const fetchCompanyESPMdata = (
  companyId: string,
  minimal: boolean = false,
): Promise<any[]> =>
  api
    .get(`company/${companyId}/espm_data/`, {
      params: {
        minimal,
      },
    })
    .then((response) => response.data as any[])
    .catch((error) => {
      console.error("There was an error fetching the company ESPM data", error);
      throw error;
    });

export interface Filing {
  id: string;
  building:
    | string
    | {
        id: string;
        name?: string;
      };
  calendar_year: number;
  status: number;
  paid: boolean;
  // Common optional fields for different submission types
  files?: string[];
  attestation_obj: {
    id: string;
    rdp: any;
    owner: any;
    attestation_type: number;
    status: number;
    file: {
      name: string;
    };
  };
  cbl_entry?: string;
  pathway?: string;
  // Use index signature for other dynamic properties
  [key: string]: unknown;
}

export interface FilingsByType {
  lL97: Filing[];
  lL88: Filing[];
}

export interface FilingCategory {
  count: number;
  items: FilingsByType;
}

export interface FilingsData {
  unstarted: FilingCategory;
  inProgress: FilingCategory;
  inReview: FilingCategory;
  outside: FilingCategory;
  submitted: FilingCategory;
  totalCount: number;
}

export const fetchCompanyFilings = (
  companyId: string,
  year?: number,
  portfolioId?: string | null,
  article?: string | null,
): Promise<FilingsData> => {
  const params = new URLSearchParams();
  if (year) params.set("calendar_year", String(year));
  if (portfolioId) params.set("portfolio", portfolioId);
  if (article) params.set("article", article);
  const qs = params.toString();
  return api
    .get(
      `company/${companyId}/get_current_filings/${qs ? `?${qs}` : ""}`,
    )
    .then((response) => response.data as FilingsData)
    .catch((error) => {
      console.error("There was an error fetching the company filings", error);
      throw error;
    });
};

export interface UserPreferencesUpdateData {
  emailAllBuildingUpdates?: boolean;
  emailNoPortfolioBuildingUpdates?: boolean;
  emailPortfolioBuildingUpdates?: boolean;
}

export const updateUserPreferences = (
  userId: string,
  preferenceData: UserPreferencesUpdateData,
): Promise<UserPreferencesType> =>
  api
    .post(`user/${userId}/preferences/`, preferenceData)
    .then((response) => response.data as UserPreferencesType)
    .catch((error) => {
      console.error("There was an error updating the user preferences", error);
      throw error;
    });
